import React, { useState, useRef, useEffect } from 'react';
import { Button } from '../ui/Button';
import { INPUT_STYLES } from '../../Constants/Const';
import { toast } from 'sonner';
import { getImagePath } from '../../Utils/Utilities';

const LoginModal = ({ isOpen, onClose, onLogin, loading = false, logo = getImagePath('/Images/QikERP.png') }) => {
  const [mobileNumber, setMobileNumber] = useState('');
  const [isClosing, setIsClosing] = useState(false);
  const [loginLoading, setLoginLoading] = useState(loading);
  const mobileInputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      // Disable scroll on body without changing scroll position
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
      
      // Prevent touch scrolling on mobile
      const preventScroll = (e) => {
        if (e.target.closest('.login-modal-content') === null) {
          e.preventDefault();
        }
      };
      
      document.addEventListener('touchmove', preventScroll, { passive: false });
      
      // Focus input
      setTimeout(() => {
        mobileInputRef.current?.focus();
      }, 100);
      
      return () => {
        document.removeEventListener('touchmove', preventScroll);
        document.body.style.overflow = 'unset';
        document.documentElement.style.overflow = 'unset';
      };
    } else {
      // Enable scroll on body
      document.body.style.overflow = 'unset';
      document.documentElement.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
      document.documentElement.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleMobileInputChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    if (value.length <= 10) {
      setMobileNumber(value);
    }
  };

  const handleLoginSubmit = (e) => {
    e.preventDefault();

    if (!mobileNumber) {
      toast.error('Please enter your mobile number');
      return;
    }

    if (mobileNumber.length !== 10) {
      toast.error('Please enter a valid 10-digit mobile number');
      return;
    }

    setLoginLoading(true);
    try {
      onLogin?.(mobileNumber);
      setMobileNumber('');
      handleCloseModal();
    } catch (error) {
      toast.error('Login failed. Please try again.');
      console.error('Login error:', error);
    } finally {
      setLoginLoading(false);
    }
  };

  const handleCloseModal = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose?.();
      setIsClosing(false);
    }, 400);
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 pt-[25%]"
      onClick={handleCloseModal}
      style={{
        animation: isClosing ? 'fadeOut 0.4s ease-out forwards' : 'fadeIn 0.3s ease-out',
      }}
    >
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        @keyframes fadeOut {
          from {
            opacity: 1;
          }
          to {
            opacity: 0;
          }
        }
        @keyframes slideDownFadeIn {
          from {
            transform: translateY(-60px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        @keyframes slideUpFadeOut {
          from {
            transform: translateY(0);
            opacity: 1;
          }
          to {
            transform: translateY(-60px);
            opacity: 0;
          }
        }
      `}</style>
      <div
        className="bg-white rounded-[5px] shadow-xl max-w-sm w-[95%] p-8 relative login-modal-content"
        style={{
          animation: isClosing ? 'slideUpFadeOut 0.3s ease-out forwards' : 'slideDownFadeIn 0.3s ease-out',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button - Protruding Out */}
        <button
          onClick={handleCloseModal}
          className="absolute -top-5 -right-5 bg-black text-white rounded-full w-10 h-10 flex items-center justify-center hover:bg-gray-800 transition-colors z-10"
        >
          ✕
        </button>

        {/* Logo Section */}
        <div className="flex flex-col items-center mb-8">
          <img
            src={logo}
            alt="Biziverse"
            className="h-28 w-auto object-contain mb-2"
          />
        </div>

        {/* Form */}
        <form onSubmit={handleLoginSubmit} className="flex flex-col gap-4">
          {/* Label */}
          <label className="text-base font-semibold text-text-primary">
            Mobile Number
          </label>

          {/* Input */}
          <div className={`${INPUT_STYLES.wrapper} w-full`}>
            <input
              ref={mobileInputRef}
              type="tel"
              value={mobileNumber}
              onChange={handleMobileInputChange}
              placeholder="Enter Your Mobile No."
              maxLength="10"
              className="w-full px-4 py-3 border-none outline-none focus:outline-none focus:ring-0 focus:border-transparent text-base bg-background-light"
              required
              disabled={loginLoading}
            />
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            variant="primary"
            className="w-fit mt-2"
            disabled={loginLoading}
          >
            {loginLoading ? 'Logging in...' : 'Next'}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default LoginModal;
